import React, { createContext, useContext, useState, useCallback } from 'react';

export interface BreadcrumbMetadata {
  tenantId?: string;
  tenantName?: string;
  tenantCode?: string;
  domainId?: string;
  domainName?: string;
  domainCode?: string;
  subjectId?: string;
  subjectName?: string;
  subjectCode?: string;
  surveyTitle?: string;
  auditTitle?: string;
  reportTitle?: string;
  templateTitle?: string;
  [key: string]: string | undefined;
}

interface BreadcrumbContextValue {
  metadata: BreadcrumbMetadata;
  setMetadata: (metadata: Partial<BreadcrumbMetadata>) => void;
  clearMetadata: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined);

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [metadata, setMetadataState] = useState<BreadcrumbMetadata>({});

  const setMetadata = useCallback((newMetadata: Partial<BreadcrumbMetadata>) => {
    setMetadataState((prev) => ({ ...prev, ...newMetadata }));
  }, []);

  const clearMetadata = useCallback(() => {
    setMetadataState({});
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ metadata, setMetadata, clearMetadata }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const useBreadcrumbContext = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const useBreadcrumbMetadata = () => {
  const context = useContext(BreadcrumbContext);
  return context?.metadata || {};
};
